import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import config from "./config";
import { loadSubscriptionPlaces } from "./places";

const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const todayIso = () => new Date().toISOString().slice(0, 10);
const normalizePhone = (value) => value.replace(/\D/g, "");
const isValidPhone = (value) => /^\d{9,15}$/.test(value);
const parseResponse = async (res) => {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (_) {
    return { error: text };
  }
};

export default function SubscriptionForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [places, setPlaces] = useState([]);
  const [carId, setCarId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [months, setMonths] = useState(1);
  const [startDate, setStartDate] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const placeId = searchParams.get("place-id") || "";

  useEffect(() => {
    let ignore = false;
    loadSubscriptionPlaces(config.apiUrl)
      .then((list) => {
        if (ignore) return;
        setPlaces(list);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const selectedPlace = useMemo(
    () => places.find((place) => place.id === placeId) || null,
    [places, placeId]
  );
  const monthlyPrice = Number(selectedPlace?.monthlyPrice || 0);
  const totalPrice = Number((monthlyPrice * Number(months || 1)).toFixed(2));
  const cleanCarId = carId.trim().replace(/\s+/g, "").toUpperCase();
  const cleanPhoneNumber = normalizePhone(phoneNumber);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!cleanCarId) {
      setError("יש להזין מספר רכב");
      return;
    }
    if (!isValidPhone(cleanPhoneNumber)) {
      setError("יש להזין מספר טלפון תקין");
      return;
    }
    if (!selectedPlace) {
      setError("יש לבחור חניון");
      return;
    }
    if (!startDate || startDate < todayIso()) {
      setError("יש לבחור תאריך התחלה מהיום והלאה");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/public-subscriptions/payment-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          carId: cleanCarId,
          phoneNumber: cleanPhoneNumber,
          userId: cleanPhoneNumber,
          placeId: selectedPlace.id,
          months: Number(months),
          startDate,
          returnBaseUrl: window.location.origin,
          syncToDevice: true,
        }),
      });
      const json = await parseResponse(res);
      if (!res.ok || !json?.paymentLink) {
        throw new Error(json?.error || "לא ניתן לפתוח תשלום");
      }
      console.log("[subscriptions] payment redirect urls", {
        appBaseUrl: json.appBaseUrl,
        successUrl: json.successUrl,
        failureUrl: json.failureUrl,
      });
      const summary = {
        ...json.summary,
        phoneNumber: json.summary?.phoneNumber || cleanPhoneNumber,
        userId: json.summary?.userId || cleanPhoneNumber,
      };
      sessionStorage.setItem("subscriptionSession", json.session);
      sessionStorage.setItem("subscriptionPhoneNumber", cleanPhoneNumber);
      sessionStorage.setItem("subscriptionSummary", JSON.stringify(summary));
      sessionStorage.setItem("subscriptionPaymentLink", json.paymentLink);
      navigate("/payment", { state: { paymentLink: json.paymentLink, summary } });
    } catch (err) {
      setError(err.message || "תקלה בפתיחת התשלום");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page" dir="rtl">
      <section className="panel">
        <img src="/logo.png" alt="Blue Parking" className="logo" />
        <h1>מנוי חניה</h1>

        {!selectedPlace ? (
          <div className="form">
            <p className="error">לא נבחר חניון תקין</p>
            <Link to="/">בחירת חניון</Link>
          </div>
        ) : (
        <form onSubmit={submit} className="form">
          <div className="selected-place">
            <span>חניון</span>
            <strong>{selectedPlace.name}</strong>
            <Link className="text-link" to="/">החלפה</Link>
          </div>

          <label>
            מספר רכב
            <input
              value={carId}
              onChange={(event) => setCarId(event.target.value)}
              placeholder="לדוגמה 1234567"
              autoComplete="off"
              required
              dir="ltr"
            />
          </label>

          <label>
            מספר טלפון
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(normalizePhone(event.target.value))}
              placeholder="לדוגמה 0501234567"
              autoComplete="tel"
              required
              dir="ltr"
            />
          </label>

          <label>
            תקופת מנוי
            <select required value={months} onChange={(event) => setMonths(Number(event.target.value))}>
              {monthOptions.map((value) => (
                <option key={value} value={value}>
                  {value} {value === 1 ? "חודש" : "חודשים"}
                </option>
              ))}
            </select>
          </label>

          <label>
            תאריך התחלה
            <input
              type="date"
              value={startDate}
              min={todayIso()}
              onChange={(event) => setStartDate(event.target.value)}
              required
            />
          </label>

          <div className="summary">
            <div>
              <span>תחילת מנוי</span>
              <strong>{startDate ? new Date(`${startDate}T00:00:00`).toLocaleDateString("he-IL") : ""}</strong>
            </div>
            <div>
              <span>מחיר לחודש</span>
              <strong>{monthlyPrice.toLocaleString("he-IL")} ₪</strong>
            </div>
            <div>
              <span>סה"כ לתשלום</span>
              <strong>{totalPrice.toLocaleString("he-IL")} ₪</strong>
            </div>
          </div>

          {error ? <p className="error">{error}</p> : null}

          <button type="submit" disabled={loading || !places.length}>
            {loading ? "פותח תשלום..." : "המשך לתשלום"}
          </button>
        </form>
        )}
      </section>
    </main>
  );
}
